import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from 'components/ui/Button';

/**
 * RolePermissionUpdateModal
 * 
 * A modal that displays when role permissions are updated.
 * This modal cannot be closed by the user and includes a countdown timer
 * before automatically logging out the user.
 */
export default function RolePermissionUpdateModal({ 
  isOpen, 
  message = 'Permissions for your role have been changed', 
  countdownSeconds = 10,
  onLogout 
}) {
  const [timeRemaining, setTimeRemaining] = useState(countdownSeconds);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Manage body class and scroll lock for preventing background interaction
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Add class and lock scroll
      document.body.classList.add('role-permission-modal-open');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Store scroll position for cleanup
      window.previousScrollY = scrollY;
    } else {
      // Restore scroll position
      const scrollY = window.previousScrollY || 0;
      
      // Remove class and restore scroll
      document.body.classList.remove('role-permission-modal-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
      
      // Clean up stored value
      delete window.previousScrollY;
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('role-permission-modal-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      const scrollY = window.previousScrollY || 0;
      window.scrollTo(0, scrollY);
      delete window.previousScrollY;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (isOpen && timeRemaining === 0 && !isLoggingOut) {
      // Auto logout when countdown reaches zero
      handleLogout();
    }
  }, [isOpen, timeRemaining, isLoggingOut]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    
    try {
      if (onLogout) {
        await onLogout();
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: redirect to login page
      window.location.href = '/login';
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-permission-title"
      aria-describedby="role-permission-description"
    >
      {/* Backdrop - cannot be clicked to close */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform">
          {/* Modal content */}
          <div className="relative rounded-2xl bg-white shadow-2xl ring-1 ring-black/10">
            {/* Warning gradient background */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 opacity-50" />
            
            {/* Content */}
            <div className="relative p-6 sm:p-8">
              {/* Header with warning icon */}
              <div className="flex items-center justify-center mb-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 shadow-lg">
                  <ExclamationTriangleIcon className="h-8 w-8 text-orange-600 animate-pulse" />
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-4">
                <h3 
                  id="role-permission-title"
                  className="text-xl font-bold text-gray-900 mb-2"
                >
                  Role Permissions Updated
                </h3>
                <p 
                  id="role-permission-description"
                  className="text-sm text-gray-600 leading-relaxed"
                >
                  {message}
                </p>
              </div>

              {/* Warning message */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-orange-800 mb-1">
                      Important Notice
                    </p>
                    <p className="text-xs text-orange-700">
                      All activities are temporarily prohibited. You will be automatically logged out for security reasons.
                    </p>
                  </div>
                </div>
              </div>

              {/* Countdown timer */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center rounded-full bg-gray-900 px-4 py-2">
                  <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-white font-mono font-bold text-lg">
                    {formatTime(timeRemaining)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Auto-logout in {timeRemaining} second{timeRemaining !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Manual logout button */}
              <div className="text-center">
                <Button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoggingOut ? (
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Logging out...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Logout Now</span>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                  )}
                </Button>
                <p className="text-xs text-gray-400 mt-2">
                  Click to logout immediately
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Prevent background interaction */}
      <style dangerouslySetInnerHTML={{
        __html: `
          body.role-permission-modal-open {
            overflow: hidden !important;
            position: fixed !important;
            width: 100% !important;
            height: 100vh !important;
            touch-action: none !important;
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior: none !important;
          }
          
          /* Block ALL background elements but allow modal */
          body.role-permission-modal-open > *:not([role="dialog"]):not([data-modal]):not(.fixed) {
            pointer-events: none !important;
            touch-action: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }
          
          /* Allow modal and all its children to be interactive */
          body.role-permission-modal-open [role="dialog"],
          body.role-permission-modal-open [data-modal],
          body.role-permission-modal-open .fixed.z-\\[9999\\],
          body.role-permission-modal-open .fixed.z-\\[9999\\] *,
          body.role-permission-modal-open [role="dialog"] *,
          body.role-permission-modal-open [data-modal] * {
            pointer-events: auto !important;
            touch-action: auto !important;
            -webkit-user-select: auto !important;
            -moz-user-select: auto !important;
            -ms-user-select: auto !important;
            user-select: auto !important;
          }
          
          /* Prevent scroll bars from appearing */
          body.role-permission-modal-open::-webkit-scrollbar {
            display: none !important;
          }
          
          body.role-permission-modal-open {
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
          }
          
          /* Prevent zoom and pinch gestures on background only */
          body.role-permission-modal-open {
            -ms-touch-action: none !important;
            touch-action: none !important;
          }
          
          /* Allow touch actions within modal */
          body.role-permission-modal-open [role="dialog"],
          body.role-permission-modal-open [data-modal],
          body.role-permission-modal-open .fixed.z-\\[9999\\] {
            -ms-touch-action: auto !important;
            touch-action: auto !important;
          }
        `
      }} />
    </div>
  );
}
